import { differenceInDays } from "date-fns";
import React, { useReducer, useRef } from "react";
import { Frequency, RRule } from "rrule";
import styled from "styled-components";
import invariant from "tiny-invariant";
import {
    isValidTimeSpent,
    withDuration,
    withScheduleDate,
    withScheduleTime,
    withoutDuration,
    withoutScheduleDate,
    withoutScheduleTime,
} from "@lifeat/shared/task-management/TaskManagementUtil";
import { TaskItemSchedulingDetails } from "@lifeat/shared/task-management/TaskSchedulingDetails";
import {
    addMinutesToDate,
    calculateMinutesBetweenDates,
    formatDateForTimeInput,
    formatMinutesToString,
    getDateIsoString,
} from "@lifeat/shared/util/DateUtil";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import CaretIconToggle from "@common/Icons/CaretIconToggle";
import IconButton from "@common/LifeAtButton/Variants/IconButton";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { DateTimeInput } from "@common/LifeAtDateTimeInput/LifeAtDateTimeInput";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import LifeAtSelectInput from "@common/LifeAtSelectInput/LifeAtSelectInput";
import { LifeAtBodySecondary, LifeAtInputLabel } from "@common/LifeAtText/LifeAtText";
import { useDebouncedInput } from "@common/LifeAtTextInput/useDebouncedInput";
import Arrow from "@icons/Arrow-Left-Icon.svg";
import RemoveFromCalendarSvg from "@icons/Calendar-With-X-Icon.svg";
import CloseIcon from "@icons/CloseIcon.svg";
import * as Tooltip from "@radix-ui/react-tooltip";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import {
    AddTaskToCalendarEvent,
    AssignTaskDueDateEvent,
    AssignTaskDurationEvent,
    AssignTaskTimeSpentEvent,
    MakeTaskRecurEvent,
} from "../AmplitudeTaskManagementEvents";

const TimeInputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
`;

const TimeSpentInputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const TaskDetailsGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(auto, 50px) minmax(260px, auto);
    grid-auto-rows: 25px;
    gap: 16px;
    align-items: center;
`;

const ArrowIcon = styled(Arrow)`
    fill: ${(props) => props.theme.textColorPrimary};
    transform: rotate(180deg);
`;

const StyledCloseIcon = styled(CloseIcon)`
    fill: ${(props) => props.theme.textColorPrimary};
`;

const RemoveFromCalIcon = styled(RemoveFromCalendarSvg)`
    stroke: ${(props) => props.theme.textColorPrimary};
`;

const CadenceTrigger = styled(DropdownMenuComponents.Trigger)`
    border-radius: ${Styles.borderRadiusSharp};
    border: 1px solid ${(props) => props.theme.borderPrimary};
    padding: 4px 8px;
`;

const CadenceTriggerLayout = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;

    width: fit-content;
    max-width: 150px;
    height: fit-content;

    padding: 2px;
    font-size: ${FontSize.normal};
    border-radius: 2px;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const CadenceContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 16px;
`;

const WarningTooltip = styled(Tooltip.Content)`
    padding: 6px;
    border-radius: ${Styles.borderRadiusSharp};
    background-color: ${(props) => props.theme.backgroundPrimary};

    box-shadow: ${(props) => props.theme.boxShadow} ${(props) => props.theme.boxShadowColor};

    font-size: ${FontSize.small};
`;

const StyledTooltipArrow = styled(Tooltip.Arrow)`
    fill: ${(props) => props.theme.backgroundPrimary};
`;

const DEFAULT_TASK_DURATION = 30;

const DURATION_LABEL = "Duration";
const DATE_LABEL = "Date";
const TIME_LABEL = "Time";
const DUE_DATE_LABEL = "Deadline";
const TIME_SPENT_LABEL = "Time spent";
const TIME_SPENT_HOURS_LABEL = "hrs";
const TIME_SPENT_MINS_LABEL = "mins";
const REMOVE_CALENDAR_BTN_LABEL = "Remove from calendar";
const DEBOUNCE_DELAY_MS = 1000;
const REPEAT_LABEL = "Repeat";
const DOES_NOT_REPEAT_LABEL = "Does not repeat";
const DAILY_LABEL = "Daily";
const WEEKLY_LABEL = "Weekly";
const MONTHLY_LABEL = "Monthly";

const ALREADY_RECURRING_WARNING = "Changing the frequency of already recurring task is not supported yet.";
const MISSING_START_DATE_WARNING = "Please set the date of the task first.";
const UNSUPPORTED_DUE_DATE_WARNING = "Setting a deadline to a recurring task is not supported yet.";

type TaskSchedulingDetailsAction =
    | { type: "UPDATE_TASK_DURATION"; durationMins: number }
    | { type: "UPDATE_TASK_DATE"; date: string }
    | { type: "UPDATE_TASK_TIME"; time: string }
    | { type: "REMOVE_TASK_DURATION" }
    | { type: "REMOVE_TASK_DATE" }
    | { type: "REMOVE_TASK_TIME" };

function schedulingDetailsReducer(
    original: TaskItemSchedulingDetails,
    action: TaskSchedulingDetailsAction,
): TaskItemSchedulingDetails {
    switch (action.type) {
        case "UPDATE_TASK_DURATION": {
            return withDuration(original, action.durationMins);
        }
        case "UPDATE_TASK_DATE": {
            return withScheduleDate(original, action.date);
        }
        case "UPDATE_TASK_TIME": {
            return withScheduleTime(original, action.time);
        }
        case "REMOVE_TASK_DURATION": {
            return withoutDuration(original);
        }
        case "REMOVE_TASK_DATE": {
            return withoutScheduleDate(original);
        }
        case "REMOVE_TASK_TIME": {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return withoutScheduleTime(original, timezone);
        }
    }
}

interface TaskSchedulingDetailsProps {
    readonly schedulingDetails?: TaskItemSchedulingDetails;
    readonly rule?: string;
    readonly timeSpentMins?: number;
    readonly amplitudeEventSource: AmplitudeEventSource;
    readonly methods: {
        updateTaskDuration: (duration: number) => Promise<void>;
        updateTaskDate: (date: string) => Promise<void>;
        updateTaskTime: (time: string) => Promise<void>;
        updateTaskTimeSpent: (timeSpent: number) => Promise<void>;
        removeTaskDuration: () => Promise<void>;
        removeTaskTime: () => Promise<void>;
        removeTaskDate: () => Promise<void> | null;
        makeTaskRecur: (frequency: Frequency) => Promise<void> | null;
        removeTaskDueDate: () => Promise<void> | null;
        updateTaskDueDate: (dueDate: string) => Promise<void> | null;
    };
}
export default function TaskSchedulingDetails(props: TaskSchedulingDetailsProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const dateInputRef = useRef<null | HTMLInputElement>(null);
    const startTimeInputRef = useRef<null | HTMLInputElement>(null);
    const endTimeInputRef = useRef<null | HTMLInputElement>(null);
    const dueDateInputRef = useRef<null | HTMLInputElement>(null);

    const [state, dispatch] = useReducer(schedulingDetailsReducer, props.schedulingDetails ?? {});
    const endTimeInput = calculateEndTime(state);

    const handleDueDateUpdate =
        props.methods.updateTaskDueDate &&
        props.methods.removeTaskDueDate &&
        ((updatedDueDate: string) => {
            if (updatedDueDate === "") {
                props.methods.removeTaskDueDate();
            } else {
                props.methods.updateTaskDueDate(updatedDueDate);
            }
            amplitudeService.logEvent(
                new AssignTaskDueDateEvent({
                    source: props.amplitudeEventSource,
                    dueDate: updatedDueDate,
                }),
            );
        });

    const [dueDateInputVal, setDueDateInputValAndDebounce] = useDebouncedInput(
        props.schedulingDetails?.dueDate ?? "",
        handleDueDateUpdate ??
            (() => {
                throw new Error("Changing due date is not supported.");
            }),
        DEBOUNCE_DELAY_MS,
    );

    const handleTimeSpentUpdate = (updatedTimeSpent: number) => {
        props.methods.updateTaskTimeSpent(updatedTimeSpent);
        amplitudeService.logEvent(
            new AssignTaskTimeSpentEvent({
                source: props.amplitudeEventSource,
                timeSpentMins: updatedTimeSpent,
            }),
        );
    };

    const [timeSpentInputVal, setTimeSpentInputValAndDebounce] = useDebouncedInput(
        props.timeSpentMins ?? 0,
        handleTimeSpentUpdate,
        DEBOUNCE_DELAY_MS,
    );
    const timeSpentHours = Math.floor(timeSpentInputVal / 60);
    const timeSpentMinutes = timeSpentInputVal % 60;

    const handleTimeSpentChange = (updatedHours: number, updatedMinutes: number) => {
        updatedHours = isNaN(updatedHours) ? 0 : updatedHours;
        updatedMinutes = isNaN(updatedMinutes) ? 0 : updatedMinutes;

        const updatedTimeSpent = updatedHours * 60 + updatedMinutes;
        if (isValidTimeSpent(updatedTimeSpent)) {
            setTimeSpentInputValAndDebounce(updatedTimeSpent);
        }
    };

    const handleDateChange = (updatedDate: string) => {
        if (updatedDate === "") {
            return handleDateRemove?.();
        }

        if (state.scheduleTime) {
            const previousTime = new Date(state.scheduleTime);
            const newTime = dateStringToLocalDate(updatedDate);
            newTime.setHours(previousTime.getHours(), previousTime.getMinutes());
            dispatch({ type: "UPDATE_TASK_TIME", time: newTime.toISOString() });
            props.methods.updateTaskTime(newTime.toISOString());
        } else {
            dispatch({ type: "UPDATE_TASK_DATE", date: updatedDate });
            props.methods.updateTaskDate(updatedDate);
        }
    };

    const handleDateRemove = props.methods.removeTaskDate
        ? () => {
              dispatch({ type: "REMOVE_TASK_DATE" });
              props.methods.removeTaskDate();
          }
        : null;

    const handleTimeRemove = () => {
        dispatch({ type: "REMOVE_TASK_TIME" });
        props.methods.removeTaskTime();
    };

    const handleDueDateRemove = () => {
        setDueDateInputValAndDebounce("");
    };

    const handleEndTimeChange = (updatedEndTime: string) => {
        invariant(state.scheduleTime);
        const startTime = new Date(state.scheduleTime);
        const newEndDate = getDateFromInput(startTime, updatedEndTime);
        const minutesDifference = calculateMinutesBetweenDates(startTime, newEndDate);
        dispatch({ type: "UPDATE_TASK_DURATION", durationMins: minutesDifference });
        props.methods.updateTaskDuration(minutesDifference);
        amplitudeService.logEvent(
            new AssignTaskDurationEvent({
                source: props.amplitudeEventSource,
                previousDuration: state.durationMins,
                duration: minutesDifference,
            }),
        );
    };

    const handleStartTimeChange = (updatedStartTime: string) => {
        const newTimeString = getDateFromInput(
            state.scheduleTime ? new Date(state.scheduleTime) : null,
            updatedStartTime,
        ).toISOString();
        dispatch({
            type: "UPDATE_TASK_TIME",
            time: newTimeString,
        });
        props.methods.updateTaskTime(newTimeString);
        amplitudeService.logEvent(
            new AddTaskToCalendarEvent({
                method: "click",
                numDaysInFuture: differenceInDays(new Date(newTimeString), new Date()),
                source: props.amplitudeEventSource,
                scheduledTime: newTimeString,
                previousTime: state.scheduleTime,
            }),
        );
    };

    const handleDurationChange = (updatedDuration: number) => {
        if (updatedDuration === 0) {
            dispatch({ type: "REMOVE_TASK_DURATION" });
            props.methods.removeTaskDuration();
        } else {
            dispatch({ type: "UPDATE_TASK_DURATION", durationMins: updatedDuration });
            props.methods.updateTaskDuration(updatedDuration);
            amplitudeService.logEvent(
                new AssignTaskDurationEvent({
                    source: props.amplitudeEventSource,
                    previousDuration: state.durationMins,
                    duration: updatedDuration,
                }),
            );
        }
    };

    const handleCadenceChange = props.methods.makeTaskRecur
        ? (frequency: Frequency) => {
              props.methods.makeTaskRecur(frequency);
              amplitudeService.logEvent(
                  new MakeTaskRecurEvent({
                      source: props.amplitudeEventSource,
                      frequency: Frequency[frequency],
                  }),
              );
          }
        : null;

    // set task date to current date
    const prefillTaskDate = () => {
        if (!displayedDate) {
            const dateString = getDateIsoString(new Date());
            dispatch({ type: "UPDATE_TASK_DATE", date: dateString });
            props.methods.updateTaskDate(dateString);
        }
    };

    // set due date to tomorrow
    const prefillTaskDueDate = () => {
        if (!dueDateInputVal) {
            const currentDate = new Date();
            currentDate.setDate(currentDate.getDate() + 1);
            setDueDateInputValAndDebounce(getDateIsoString(currentDate));
        }
    };

    // set start time to now and duration to 30 minutes
    const prefillTimeAndDuration = () => {
        if (!state.scheduleTime) {
            const prefillTime = state.scheduleDate ? dateStringToLocalDate(state.scheduleDate) : new Date();
            const prefillTimeString = prefillTime.toISOString();
            dispatch({
                type: "UPDATE_TASK_TIME",
                time: prefillTimeString,
            });
            props.methods.updateTaskTime(prefillTimeString);
        }
        if (!state.durationMins) {
            dispatch({ type: "UPDATE_TASK_DURATION", durationMins: DEFAULT_TASK_DURATION });
            props.methods.updateTaskDuration(DEFAULT_TASK_DURATION);
        }
    };

    const durationOptions = generateOptions(state.durationMins ?? 0);
    const displayedDate =
        state.scheduleDate ?? (state.scheduleTime ? getDateIsoString(new Date(state.scheduleTime)) : "");

    return (
        <>
            <TaskDetailsGrid>
                <LifeAtInputLabel>{DATE_LABEL}</LifeAtInputLabel>
                <TimeInputRow>
                    {/* Note: input starts out as a text input so that we can display placeholder text. Input type is
                changed to date or time on focus (https://www.geeksforgeeks.org/how-to-set-placeholder-value-for-input-type-date-in-html-5/)*/}
                    <DateTimeInput
                        ref={dateInputRef}
                        id={"task-start-date"}
                        type={displayedDate ? "date" : "text"}
                        value={displayedDate}
                        onChange={(e) => handleDateChange(e.target.value)}
                        onFocus={() => prefillTaskDate()}
                        onClick={() => dateInputRef.current?.showPicker()}
                        placeholder={"00/00/00"}
                        widthPx={130}
                    />
                    {displayedDate && handleDateRemove && (
                        <IconButton onClick={() => handleDateRemove()} variant={"normal"} size={"small"}>
                            <StyledCloseIcon />
                        </IconButton>
                    )}
                </TimeInputRow>
                <LifeAtInputLabel>{TIME_LABEL}</LifeAtInputLabel>
                <TimeInputRow>
                    <DateTimeInput
                        ref={startTimeInputRef}
                        id={"task-start-time"}
                        type={state.scheduleTime ? "time" : "text"}
                        value={state.scheduleTime ? formatDateForTimeInput(new Date(state.scheduleTime)) : ""}
                        onChange={(e) => handleStartTimeChange(e.target.value)}
                        onFocus={() => prefillTimeAndDuration()}
                        onClick={() => startTimeInputRef.current?.showPicker()}
                        placeholder={"00:00"}
                        widthPx={95}
                    />
                    <ArrowIcon />
                    <DateTimeInput
                        ref={endTimeInputRef}
                        id={"task-end-time"}
                        type={endTimeInput ? "time" : "text"}
                        value={endTimeInput ? formatDateForTimeInput(endTimeInput) : ""}
                        onChange={(e) => handleEndTimeChange(e.target.value)}
                        onFocus={() => prefillTimeAndDuration()}
                        onClick={() => endTimeInputRef.current?.showPicker()}
                        placeholder={"00:00"}
                        widthPx={95}
                    />
                    {state.scheduleTime && (
                        <IconButton onClick={() => handleTimeRemove()} variant={"normal"} size={"small"}>
                            <StyledCloseIcon />
                        </IconButton>
                    )}
                </TimeInputRow>
                <Tooltip.Root delayDuration={0}>
                    <LifeAtInputLabel>{DUE_DATE_LABEL}</LifeAtInputLabel>
                    <TimeInputRow>
                        {/* Note: input starts out as a text input so that we can display placeholder text. Input type is
                changed to date or time on focus (https://www.geeksforgeeks.org/how-to-set-placeholder-value-for-input-type-date-in-html-5/)*/}
                        <Tooltip.Trigger asChild>
                            <DateTimeInput
                                ref={dueDateInputRef}
                                id={"task-due-date"}
                                disabled={!handleDueDateUpdate}
                                type={dueDateInputVal ? "date" : "text"}
                                value={dueDateInputVal}
                                onChange={(e) => setDueDateInputValAndDebounce(e.target.value)}
                                onFocus={() => prefillTaskDueDate()}
                                onClick={() => dueDateInputRef.current?.showPicker()}
                                placeholder={"00/00/00"}
                                widthPx={130}
                            />
                        </Tooltip.Trigger>
                        {dueDateInputVal && (
                            <IconButton onClick={() => handleDueDateRemove()} variant={"normal"} size={"small"}>
                                <StyledCloseIcon />
                            </IconButton>
                        )}
                        {!handleDueDateUpdate && (
                            <WarningTooltip>
                                <StyledTooltipArrow />
                                {UNSUPPORTED_DUE_DATE_WARNING}
                            </WarningTooltip>
                        )}
                    </TimeInputRow>
                </Tooltip.Root>
                <LifeAtInputLabel htmlFor={"task-duration"}>{DURATION_LABEL}</LifeAtInputLabel>
                <TimeInputRow>
                    <LifeAtSelectInput
                        id={"task-duration"}
                        options={durationOptions}
                        value={state.durationMins ?? 0}
                        placeholder="Duration"
                        onChange={(duration) => handleDurationChange(duration || 0)}
                    />
                </TimeInputRow>
            </TaskDetailsGrid>
            <TimeSpentInputRow>
                <LifeAtInputLabel>{TIME_SPENT_LABEL}</LifeAtInputLabel>
                <DateTimeInput
                    id={"task-time-spent-hours"}
                    type={"number"}
                    value={timeSpentHours.toString()}
                    onChange={(e) => handleTimeSpentChange(parseInt(e.target.value), timeSpentMinutes)}
                    min={0}
                    widthPx={45}
                />
                <LifeAtBodySecondary>{TIME_SPENT_HOURS_LABEL}</LifeAtBodySecondary>

                <DateTimeInput
                    id={"task-time-spent-minutes"}
                    type={"number"}
                    value={timeSpentMinutes.toString()}
                    onChange={(e) => handleTimeSpentChange(timeSpentHours, parseInt(e.target.value))}
                    widthPx={45}
                />
                <LifeAtBodySecondary>{TIME_SPENT_MINS_LABEL}</LifeAtBodySecondary>
            </TimeSpentInputRow>
            <Tooltip.Root delayDuration={0}>
                <CadenceContainer>
                    <LifeAtInputLabel>{REPEAT_LABEL}</LifeAtInputLabel>
                    <DropdownMenuComponents.Root>
                        <Tooltip.Trigger asChild>
                            <CadenceTrigger disabled={!handleCadenceChange}>
                                <CadenceTriggerLayout>
                                    {props.rule ? ruleToLabel(props.rule) : DOES_NOT_REPEAT_LABEL}
                                    <CaretIconToggle showOpen={false} />
                                </CadenceTriggerLayout>
                            </CadenceTrigger>
                        </Tooltip.Trigger>
                        <DropdownMenuComponents.Content>
                            <DropdownMenuComponents.Item
                                onClick={() => {
                                    handleCadenceChange?.(RRule.DAILY);
                                }}
                            >
                                {DAILY_LABEL}
                            </DropdownMenuComponents.Item>
                            <DropdownMenuComponents.Item
                                onClick={() => {
                                    handleCadenceChange?.(RRule.WEEKLY);
                                }}
                            >
                                {WEEKLY_LABEL}
                            </DropdownMenuComponents.Item>
                            <DropdownMenuComponents.Item
                                onClick={() => {
                                    handleCadenceChange?.(RRule.MONTHLY);
                                }}
                            >
                                {MONTHLY_LABEL}
                            </DropdownMenuComponents.Item>
                        </DropdownMenuComponents.Content>
                    </DropdownMenuComponents.Root>
                </CadenceContainer>
                {!handleCadenceChange && (
                    <WarningTooltip>
                        <StyledTooltipArrow />
                        {props.rule == null ? MISSING_START_DATE_WARNING : ALREADY_RECURRING_WARNING}
                    </WarningTooltip>
                )}
            </Tooltip.Root>
            <LifeAtButtonV2 onClick={() => handleTimeRemove()} size={"small"}>
                <RemoveFromCalIcon />
                {REMOVE_CALENDAR_BTN_LABEL}
            </LifeAtButtonV2>
        </>
    );
}

/**
 * Returns a Date object with the date set to the date of the input date and the time set to the time of the input time.
 * @param date This is required because we cannot build a Date object with just a time.
 * @param timeInput time string in the format HH:MM
 */
const getDateFromInput = (date: Date | null, timeInput: string) => {
    const ret = date ? new Date(date) : new Date();
    const timeArray = timeInput.split(":");
    ret.setHours(parseInt(timeArray[0] || "0"), parseInt(timeArray[1] || "0"));
    return ret;
};

const calculateEndTime = (schedulingDetails: TaskItemSchedulingDetails): Date | null => {
    if (schedulingDetails.scheduleTime && schedulingDetails.durationMins) {
        return addMinutesToDate(new Date(schedulingDetails.scheduleTime), schedulingDetails.durationMins);
    } else {
        return null;
    }
};

/**
 * @example dateStringToLocalDate("2021-10-10") returns a Date object with the date set to October 10, 2021 local time.
 */
function dateStringToLocalDate(dateString: string): Date {
    const [year, month, date] = dateString.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(date));
}

const generateOptions = (currentDurationMins: number) => {
    const options: { label: string; value: number }[] = [];

    // Add 5-minute intervals from 0 to 60 minutes
    for (let i = 0; i < 60; i += 5) {
        options.push({ label: i === 0 ? "None" : formatMinutesToString(i), value: i });
    }

    // Add 30-minute intervals from 60 to 480 minutes
    for (let i = 60; i <= 480; i += 30) {
        options.push({ label: formatMinutesToString(i), value: i });
    }

    // append the current duration to list so that it shows up as a valid selection option
    // to handle the case where the duration is controlled by the start and end time, or
    // dragging on the calendar and resulting value is not in the dropdown
    const currentDurationExists = options.find((option) => option.value === currentDurationMins);
    if (!currentDurationExists) {
        options.push({ label: formatMinutesToString(currentDurationMins), value: currentDurationMins });
    }

    return options;
};

function ruleToLabel(rule: string): string {
    const rrule = RRule.fromString(rule);
    switch (rrule.options.freq) {
        case RRule.DAILY:
            return DAILY_LABEL;
        case RRule.WEEKLY:
            return WEEKLY_LABEL;
        case RRule.MONTHLY:
            return MONTHLY_LABEL;
        default:
            return DOES_NOT_REPEAT_LABEL;
    }
}
